import { useState, useEffect, useMemo } from 'react';
import { useMemoizedFn, useMount } from 'ahooks';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Input, Tag, Tooltip } from 'antd';
import type { CheckboxOptionType } from 'antd';
import { Descendant } from 'slate';
import { getMsgOfNoData } from 'components/TemplateEditor/mustacheUtils';
import type { MsgType } from 'components/TemplateEditor/mustacheUtils';
import { useCustomUpload, useCustomVideoUpload } from 'hooks/business/useUploadResource';
import { useTranslate } from '@tolgee/react';
import useStore from 'views/Setting/Phrase/hooks/useStore';
import { ImageInfo, CustomSlateNodeType } from 'components/TemplateEditor/types';
import { ImageElement, LogisticsElement } from 'components/TemplateEditor/slateTypes';
import type { DescendantExecuteContext } from 'views/Aide/Scenario/PolicyForm';
import { getSlateEmptyValue } from 'components/TemplateEditor/utils/slateNodeHelper';
import ExecuteContext from './ExecuteContext';
import FileUpload, { WrapStyle } from './ExecuteContext/FileUpload';
import MsgTypesRadioGroup from './ExecuteContext/MsgTypesRadioGroup';
import { RecommendTemplates, RecommendTemplates2 } from './RecommendTemplates';
import { ScenarioType } from './TranslateTool';
import { FollowCardMessage } from 'components/Dialogue';
import { Concept } from 'components/TemplateEditor/toolbarPlugins/ConceptMenu';
import ContentItemListField from '../NewPolicy/ContentItemListField';
import MercadoExecuteContexts from './MercadoExecuteContexts';
import { LogisticsPreview } from 'components/EditorPreviewer';
import styled from '@emotion/styled';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';

export const getMsgLength = (content: Descendant[]) => {
  return content.reduce((res, cur) => {
    if (cur.type === CustomSlateNodeType.PARAGRAPH) {
      return res + 1;
    }

    if (cur.type === CustomSlateNodeType.IMAGE && cur.images?.length) {
      return res + 1;
    }

    return res;
  }, 0);
};

type TExtraFunc = (msgType: MsgType) => JSX.Element;

export interface ExecuteContextsFieldV2Props {
  scenarioType?: ScenarioType;
  region: General.UpperCaseRegion;
  platform?: General.PlatformUpperCase;
  msgTypes: MsgType[];
  value?: DescendantExecuteContext;
  onChange: (eventOrValue: DescendantExecuteContext) => void;
  error: string | Record<string, string> | undefined;
  extra?: JSX.Element | TExtraFunc;
  sendPoint?: Marketing.CancelTradeSendPoint;
  shippingStatus?: Marketing.ShippingStatus[];
  refresh?: number;
  recommendTemplates?: Marketing.Policy['recommendTemplates'];
  langTemplatesMap?: Aide.LangTemplatesMap;
  supportTranslate?: boolean;
  replyOption?: Marketing.ReplyOption;
  allowDividing?: boolean;
  isQa?: boolean;
  showMsgTypes?: boolean;
  extraConcepts?: Concept[];
  contentItemList?: Aide.Policy['contentItemList'];
  isKnowledge?: boolean;
  disabled?: boolean;
  isReply?: boolean;
}

function ExecuteContextsFieldV2Component(props: ExecuteContextsFieldV2Props) {
  const {
    scenarioType,
    region,
    platform,
    msgTypes,
    value = {
      content: getSlateEmptyValue(),
      translatedContent: getSlateEmptyValue(),
    },
    onChange,
    error,
    extra,
    refresh = 1,
    recommendTemplates,
    langTemplatesMap,
    supportTranslate,
    replyOption,
    allowDividing = true,
    isQa,
    showMsgTypes = true,
    extraConcepts,
    sendPoint,
    contentItemList,
    isKnowledge,
    disabled,
    isReply,
  } = props;

  const { t } = useTranslate();

  const customUpload = useCustomUpload();
  const customVideoUpload = useCustomVideoUpload();

  const store = useStore(platform, region);

  const [checkedMsgTypes, setCheckedMsgTypes] = useState<MsgType[]>([]);

  const msgTypesDeal = useMemoizedFn((value: DescendantExecuteContext) => {
    const content = filterContent(value.content, msgTypes);
    const translatedContent = filterContent(value.translatedContent, msgTypes);

    const types: MsgType[] = [];

    let cntHasText = false;
    let cntHasTextIndex = -1;
    content.forEach((v, i) => {
      let t: MsgType | undefined;
      if (v.type === 'paragraph' || v.type === 'eos') {
        t = 'text';
        cntHasText = true;
        cntHasTextIndex = i;
      } else if (v.type === 'image') {
        t = v.children[0].text as MsgType;
      } else if (v.type === 'item') {
        t = 'item';
      } else if (v.type === CustomSlateNodeType.LOGISTICS) {
        t = 'logistics';
      }

      if (t && !types.includes(t)) {
        types.push(t as MsgType);
      }
    });

    let cntTransHasText = false;
    if (supportTranslate) {
      translatedContent.forEach((v, i) => {
        let t: MsgType | undefined;
        if (v.type === 'paragraph' || v.type === 'eos') {
          t = 'text';
          cntTransHasText = true;
          if (!cntHasText) {
            content.splice(i, 0, getMsgOfNoData('text'));
          }
        } else if (v.type === 'image') {
          t = v.children[0].text as MsgType;
        } else if (v.type === 'item') {
          t = 'item';
        } else if (v.type === CustomSlateNodeType.LOGISTICS) {
          t = 'logistics';
        }

        if (t && !types.includes(t)) {
          types.splice(i, 0, t as MsgType);
        }
      });

      if (cntHasTextIndex !== -1 && !cntTransHasText) {
        translatedContent.splice(cntHasTextIndex, 0, getMsgOfNoData('text'));
      }
    }

    if (types.length === 0) {
      types.push('text');
    }

    onChange({ content, translatedContent });

    return types;
  });

  const msgLength = useMemo(() => {
    if (platform !== 'LAZADA') {
      return 0;
    }

    return getMsgLength(value.content);
  }, [platform, value.content]);

  const lazadaMsgLimit = platform === 'LAZADA' && msgLength >= 5;

  useMount(() => {
    setCheckedMsgTypes(msgTypesDeal(value));
  });

  useEffect(() => {
    if (refresh > 1) {
      setCheckedMsgTypes(msgTypesDeal(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const errUI = (error as any)?.text ? (
    <Tag color="error" style={{ border: 'none', background: 'none', marginBottom: '16px' }}>
      {(error as any)?.text}
    </Tag>
  ) : null;

  const onTemplateSelect = useMemoizedFn((content: Descendant[]) => {
    const next = mergePartialValue(value, { content, translatedContent: [] }, 'text');
    if (scenarioType === 'INVITE_FOLLOWER' && !checkedMsgTypes.includes('follow')) {
      const content = [
        {
          type: 'image',
          images: [
            {
              imageId: 'https://resource.zhisuitech.com/media/bff209074de9491181e12bbbd14a7d7f.png',
            },
          ],
          children: [{ text: 'follow' }],
        },
      ] as any;
      onChange(
        mergePartialValue(
          next,
          {
            content,
            translatedContent: content,
          },
          'follow',
        ),
      );
      setCheckedMsgTypes([...checkedMsgTypes, 'follow']);
    } else {
      onChange(next);
    }
  });

  return (
    <>
      {checkedMsgTypes.map((msgType, idxType) => {
        const valuePartial = getValueByType(value, msgType);

        const msgTypeOpts: CheckboxOptionType[] = [];

        msgTypes.forEach((v) => {
          msgTypeOpts.push({
            label: v,
            value: v,
            disabled: v === msgType ? false : checkedMsgTypes.includes(v),
          });
        });

        let MsgTypeUI = null;

        if (msgType === 'text') {
          MsgTypeUI = (
            <div style={{ marginBottom: '16px' }}>
              <ExecuteContext
                disabled={disabled}
                scenarioType={scenarioType}
                platform={platform}
                region={region}
                allowDividing={allowDividing && !lazadaMsgLimit}
                executeContext={valuePartial}
                onExecuteContextChange={(v) => {
                  onChange(mergePartialValue(value, v, msgType));
                }}
                extra={typeof extra === 'function' ? extra(msgType) : extra}
                supportTranslate={supportTranslate}
                replyOption={replyOption}
                extraConcepts={extraConcepts}
              />
              {!langTemplatesMap && (
                <RecommendTemplates
                  region={region}
                  recommendTemplates={recommendTemplates}
                  onTemplateSelect={onTemplateSelect}
                />
              )}
              {sendPoint !== 'IN_CANCEL' && (
                <RecommendTemplates2
                  region={region}
                  langTemplatesMap={langTemplatesMap}
                  onTemplateSelect={onTemplateSelect}
                />
              )}
            </div>
          );
        } else if (msgType === 'image') {
          MsgTypeUI = (
            <div className={WrapStyle}>
              <FileUpload
                key={`${msgType}-${refresh}`}
                disabled={disabled}
                platform={platform}
                region={region}
                fileType={msgType}
                fileSource={valuePartial.content.reduce((arr, v) => {
                  arr.push(...(v as ImageElement).images);
                  return arr;
                }, [] as ImageInfo[])}
                onImagesChange={(v) => {
                  let content: Descendant[] = v.map((v) => {
                    return {
                      type: 'image',
                      images: [
                        {
                          ...v,
                          imageId: v.imageId,
                        },
                      ],
                      children: [{ text: msgType }],
                    };
                  }) as Descendant[];

                  if (content.length === 0) {
                    content = [getMsgOfNoData(msgType)];
                  }

                  onChange(
                    mergePartialValue(
                      value,
                      {
                        content,
                        translatedContent: content,
                      } as DescendantExecuteContext,
                      msgType,
                    ),
                  );
                }}
                maxCount={10}
                addable={!lazadaMsgLimit}
                isQa={isQa}
                customRequest={(options) =>
                  customUpload(options, {
                    storeId: platform ? `${store?.id}` : undefined,
                    fileType: 'IMAGE',
                    reqSource: 'TEMPLATE_EDITOR',
                    uploadResourceCentre: !platform,
                  })
                }
              />
            </div>
          );
        } else if (msgType === 'video') {
          MsgTypeUI = (
            <div className={WrapStyle}>
              <FileUpload
                key={`${msgType}-${refresh}`}
                disabled={disabled}
                platform={platform}
                region={region}
                fileType={msgType}
                fileSource={valuePartial.content.reduce((arr, v) => {
                  arr.push(...(v as ImageElement).images);
                  return arr;
                }, [] as ImageInfo[])}
                onImagesChange={(v) => {
                  let content: Descendant[] = v.map((v) => {
                    return {
                      type: 'image',
                      images: [
                        {
                          ...v,
                          imageId: v.imageId,
                        },
                      ],
                      children: [{ text: msgType }],
                    };
                  }) as Descendant[];

                  if (content.length === 0) {
                    content = [getMsgOfNoData(msgType)];
                  }

                  onChange(
                    mergePartialValue(
                      value,
                      {
                        content,
                        translatedContent: content,
                      } as DescendantExecuteContext,
                      msgType,
                    ),
                  );
                }}
                maxCount={10}
                addable={!lazadaMsgLimit}
                customRequest={(options) =>
                  customVideoUpload(
                    options,
                    platform ? `${store?.id}` : undefined,
                    platform ? region : undefined,
                    !platform,
                  )
                }
              />
            </div>
          );
        } else if (msgType === 'sticker') {
          MsgTypeUI = (
            <div className={WrapStyle}>
              <FileUpload
                key={`${msgType}-${refresh}`}
                platform={platform}
                region={region}
                fileType={msgType}
                fileSource={valuePartial.content.reduce((arr, v) => {
                  arr.push(...(v as ImageElement).images);
                  return arr;
                }, [] as ImageInfo[])}
                onImagesChange={(v) => {
                  let content: Descendant[] = v.map((v) => {
                    return {
                      type: 'image',
                      images: [
                        {
                          ...v,
                          imageId: v.imageId,
                        },
                      ],
                      children: [{ text: msgType }],
                    };
                  }) as Descendant[];

                  if (content.length === 0) {
                    content = [getMsgOfNoData(msgType)];
                  }

                  onChange(
                    mergePartialValue(
                      value,
                      {
                        content,
                        translatedContent: content,
                      } as DescendantExecuteContext,
                      msgType,
                    ),
                  );
                }}
                maxCount={3}
                addable={!lazadaMsgLimit}
              />
            </div>
          );
        } else if (msgType === 'follow') {
          MsgTypeUI =
            platform === 'LAZADA' ? (
              <div className={WrapStyle}>
                <FollowCardMessage storeName={t('marketing.店铺名称')} />
              </div>
            ) : (
              <div className={WrapStyle}>
                <FileUpload
                  key={`${msgType}-${refresh}`}
                  platform={platform}
                  region={region}
                  fileType={msgType}
                  disabled
                  fileSource={
                    platform === 'SHOPEE'
                      ? ([
                          {
                            // 带关注礼
                            imageId:
                              {
                                ID: 'https://down-sg.img.susercontent.com/sg-11134209-7rbkv-lns2vyoau5l47c',
                                VN: 'https://down-sg.img.susercontent.com/sg-11134209-7rbly-lns2x38bg6px4a',
                                TH: 'https://down-sg.img.susercontent.com/sg-11134209-7rbmq-lns2uuknir9o20',
                                MY: 'https://down-sg.img.susercontent.com/sg-11134209-8259c-mqhj5k2ap89ba2',
                              }[region as string] ||
                              'https://down-sg.img.susercontent.com/sg-11134209-7rbnf-lnsi7sm5fu0g92',
                          },
                          {
                            // 不带关注礼
                            imageId:
                              {
                                ID: 'https://down-sg.img.susercontent.com/sg-11134209-7rbkp-lns2vikycjwo5f',
                                VN: 'https://down-sg.img.susercontent.com/sg-11134209-7rbl6-lns2wfcqe2whfc',
                                TH: 'https://down-sg.img.susercontent.com/sg-11134209-7rbkz-lns2u7lt65k8c2',
                                MY: 'https://down-sg.img.susercontent.com/sg-11134209-8258v-mqhixq5sk3rf4c',
                              }[region as string] ||
                              'https://cf.shopee.co.id/file/9f35ce1a415f46ec9664a8cf01cb1e8c',
                          },
                        ] as ImageInfo[])
                      : isTiKTokUpperCase(platform)
                      ? [
                          {
                            imageId:
                              {
                                VN: 'https://resource.zhisuitech.com/media/3860049/56812/a3f4c75daa684055b7354efa9f1099e6-46ae235ed9023a106ed9984ba247b7b3.png',
                                ID: 'https://resource.zhisuitech.com/media/3860049/56812/aabdfeef66544000bc59acaacf0173a8-d2b5ca33bd970f64a6301fa75ae2eb22.png',
                                TH: 'https://resource.zhisuitech.com/media/3860049/56812/9a4ff652de8842b2a1afde9b5115dda6-681b967728fdfecb8c5912598087e416.png',
                                MY: 'https://resource.zhisuitech.com/media/3860049/56812/0a463402ebf54b19926f90ee2a4cd4bf-e1e35ffd2e6fa5d6b0d5bf9f53a6182b.png',
                                PH: 'https://resource.zhisuitech.com/media/3860049/56812/d5d14f99cc034a75be2a8140fba21997-da6ef203f0a19b996c089baee0c5bcd2.png',
                              }[region as string] ||
                              'https://resource.zhisuitech.com/media/4192730/56812/6ff1e23fec234afdb660d0c1d10d87ab-e1e35ffd2e6fa5d6b0d5bf9f53a6182b.png',
                          },
                        ]
                      : []
                  }
                  onImagesChange={() => {
                    const content = [
                      {
                        type: 'image',
                        images: [
                          {
                            imageId:
                              'https://resource.zhisuitech.com/media/bff209074de9491181e12bbbd14a7d7f.png',
                          },
                        ],
                        children: [{ text: msgType }],
                      },
                    ];

                    onChange(
                      mergePartialValue(
                        value,
                        {
                          content,
                          translatedContent: content,
                        } as DescendantExecuteContext,
                        msgType,
                      ),
                    );
                  }}
                  maxCount={2}
                />
              </div>
            );
        } else if (msgType === 'qrcode') {
          MsgTypeUI = (
            <div className={WrapStyle}>
              <FileUpload
                key={`${msgType}-${refresh}`}
                platform={platform}
                region={region}
                fileType={msgType}
                disabled
                fileSource={
                  [
                    {
                      imageId:
                        'https://resource.zhisuitech.com/media/34157c39d94a433d8926f05b2d8c7977.png',
                    },
                  ] as ImageInfo[]
                }
                onImagesChange={() => {
                  const content = [
                    {
                      type: 'image',
                      images: [
                        {
                          imageId:
                            'https://resource.zhisuitech.com/media/34157c39d94a433d8926f05b2d8c7977.png',
                        },
                      ],
                      children: [{ text: msgType }],
                    },
                  ];

                  onChange(
                    mergePartialValue(
                      value,
                      {
                        content,
                        translatedContent: content,
                      } as DescendantExecuteContext,
                      msgType,
                    ),
                  );
                }}
                maxCount={1}
              />
            </div>
          );
        } else if (msgType === 'item') {
          MsgTypeUI = <ContentItemListField region={region} isKnowledge={isKnowledge} />;
        } else if (msgType === 'logistics') {
          MsgTypeUI = (
            <LogisticsContent>
              <Input.TextArea
                value={(valuePartial.content[0] as LogisticsElement).preText}
                onChange={({ target: { value: preText } }) => {
                  const content = [
                    {
                      type: CustomSlateNodeType.LOGISTICS,
                      preText,
                    } as LogisticsElement,
                  ];
                  onChange(
                    mergePartialValue(
                      value,
                      {
                        content,
                        translatedContent: content,
                      },
                      msgType,
                    ),
                  );
                }}
              />
              <LogisticsPreview />
            </LogisticsContent>
          );
        }

        const toPos = (posNum: number) => {
          let toIdx = posNum;
          let moveIdx = idxType;
          if (posNum > idxType) {
            toIdx = idxType;
            moveIdx = posNum;
          }

          const toPos = getFromPosByType(value, checkedMsgTypes[toIdx]);

          const moveEles = getValueByType(value, checkedMsgTypes[moveIdx]);

          // eslint-disable-next-line no-param-reassign
          value.content = excludeMsgType(value.content, checkedMsgTypes[moveIdx]);
          // eslint-disable-next-line no-param-reassign
          value.translatedContent = excludeMsgType(
            value.translatedContent,
            checkedMsgTypes[moveIdx],
          );

          value.content.splice(toPos.content, 0, ...moveEles.content);
          value.translatedContent.splice(toPos.translatedContent, 0, ...moveEles.translatedContent);
        };

        const msgTypesRadioGroup = showMsgTypes && (
          <>
            <MsgTypesRadioGroup
              disabled={disabled}
              platform={platform}
              msgTypeOpts={msgTypeOpts}
              value={msgType}
              idxType={idxType}
              checkedMsgTypesLen={checkedMsgTypes.length}
              moveTop={() => {
                if (idxType === 0) {
                  return;
                }
                toPos(0);
                setCheckedMsgTypes(msgTypesDeal(value));
              }}
              moveUp={() => {
                if (idxType === 0) {
                  return;
                }
                toPos(idxType - 1);
                setCheckedMsgTypes(msgTypesDeal(value));
              }}
              moveDown={() => {
                if (idxType === checkedMsgTypes.length - 1) {
                  return;
                }
                toPos(idxType + 1);
                setCheckedMsgTypes(msgTypesDeal(value));
              }}
              delMsg={() => {
                setCheckedMsgTypes(msgTypesDeal(mergePartialValue(value, undefined, msgType)));
              }}
              switchMsgType={(newType: MsgType) => {
                setCheckedMsgTypes(
                  msgTypesDeal(mergePartialValue(value, undefined, msgType, newType, region)),
                );
              }}
              valuePartial={valuePartial}
              curChkValue={getValueByType(value, msgType)}
              contentItemList={contentItemList}
              isKnowledge={isKnowledge}
            />
          </>
        );

        return (
          <div key={msgType}>
            {msgTypesRadioGroup}
            {MsgTypeUI}
          </div>
        );
      })}

      {errUI}
      {(() => {
        if (msgTypes.length <= 1) {
          return null;
        }

        const text = t(
          isReply
            ? '添加消息（回复内容可含多条不同类型消息）'
            : '添加消息（发送内容可含多条不同类型消息）',
        );

        if (lazadaMsgLimit) {
          return (
            <Tooltip
              title={t(
                'aide.因Lazada平台限制，买家不回复消息最多允许店铺发送5条/天消息。策略中的发送内容最多可添加5条。',
              )}
            >
              <Button block disabled type="dashed" icon={<PlusOutlined />}>
                {text}
              </Button>
            </Tooltip>
          );
        }

        return (
          <Button
            block
            type="dashed"
            icon={<PlusOutlined />}
            disabled={checkedMsgTypes.length === msgTypes.length || disabled}
            onClick={() => {
              msgTypes.some((v) => {
                if (checkedMsgTypes.includes(v)) {
                  return false;
                }
                value.content.push(getMsgOfNoData(v, region));
                value.translatedContent.push(getMsgOfNoData(v, region));
                setCheckedMsgTypes(msgTypesDeal(value));
                return true;
              });
            }}
          >
            {text}
          </Button>
        );
      })()}
    </>
  );
}

const validContent = (content: Descendant[], type: MsgType) => {
  return content.filter((v) => {
    if (type === 'text') {
      // && (v.children[0] as CustomText)?.text !== ''
      if (v.type === 'paragraph' || v.type === 'eos') {
        return true;
      }
      return false;
    }

    if (type === 'item' && v.type === 'item') {
      return true;
    }

    if (type === 'logistics' && v.type === CustomSlateNodeType.LOGISTICS) {
      return true;
    }

    if (
      v.type === CustomSlateNodeType.IMAGE &&
      v.children[0]?.text === type &&
      v.images[0]?.imageId !== ''
    ) {
      return true;
    }

    return false;
  });
};

export const getValueByType = (value: DescendantExecuteContext, type: MsgType) => {
  return {
    content: validContent(value.content, type),
    translatedContent: validContent(value.translatedContent, type),
  };
};

const getFromPosByType = (value: DescendantExecuteContext, type: MsgType) => {
  const p1 = getFromPosByTypeInner(value.content, type);
  const p2 = getFromPosByTypeInner(value.translatedContent, type);
  const p = value.content.length > value.translatedContent.length ? p1 : p2;
  return {
    content: p,
    translatedContent: p,
  };
};

const getFromPosByTypeInner = (cntList: Descendant[], msgType: MsgType) => {
  let from = -1;
  cntList.some((v, i) => {
    let t: MsgType | undefined;
    if (v.type === 'paragraph' || v.type === 'eos') {
      t = 'text';
    } else if (v.type === 'image') {
      t = v.children[0].text as MsgType;
    } else if (v.type === 'item') {
      t = 'item';
    } else if (v.type === CustomSlateNodeType.LOGISTICS) {
      t = 'logistics';
    }

    if (t === msgType && from === -1) {
      from = i;
      return true;
    }
    return false;
  });

  if (from === -1) {
    from = cntList.length;
  }
  return from;
};

const excludeMsgType = (cntList: Descendant[], msgType: MsgType) => {
  const newList = cntList.filter((v) => {
    let t: MsgType | undefined;
    if (v.type === 'paragraph' || v.type === 'eos') {
      t = 'text';
    } else if (v.type === 'image') {
      t = v.children[0].text as MsgType;
    } else if (v.type === 'item') {
      t = 'item';
    } else if (v.type === CustomSlateNodeType.LOGISTICS) {
      t = 'logistics';
    }

    if (t === msgType) {
      return false;
    }
    return true;
  });
  return newList;
};

export const mergePartialValue = (
  value: DescendantExecuteContext,
  partialValue: DescendantExecuteContext | undefined,
  msgType: MsgType,
  newMsgType?: MsgType,
  region?: General.UpperCaseRegion,
) => {
  const valuePos = getFromPosByType(value, msgType);
  // eslint-disable-next-line no-param-reassign
  value.content = excludeMsgType(value.content, msgType);
  // eslint-disable-next-line no-param-reassign
  value.translatedContent = excludeMsgType(value.translatedContent, msgType);

  if (partialValue) {
    value.content.splice(valuePos.content, 0, ...partialValue.content);
    value.translatedContent.splice(
      valuePos.translatedContent,
      0,
      ...partialValue.translatedContent,
    );
  } else if (newMsgType) {
    value.content.splice(valuePos.content, 0, getMsgOfNoData(newMsgType, region));
    value.translatedContent.splice(
      valuePos.translatedContent,
      0,
      getMsgOfNoData(newMsgType, region),
    );
  }

  const v = {
    content: value.content,
    translatedContent: value.translatedContent,
  };
  return v;
};

const filterContent = (content: Descendant[], msgTypes: MsgType[]) => {
  return content.filter((v) => {
    let t: MsgType | undefined;
    if (['eos', 'paragraph'].includes(v.type as string)) {
      t = 'text';
    } else if (v.type === 'image') {
      t = v.children[0].text as MsgType;
    } else if (v.type === 'item') {
      t = 'item';
    } else if (v.type === CustomSlateNodeType.LOGISTICS) {
      t = 'logistics';
    }

    if (t && msgTypes.includes(t)) {
      return true;
    }

    return false;
  });
};

export default function ExecuteContextsFieldV2(props: ExecuteContextsFieldV2Props) {
  return props.platform === 'MERCADO' ? (
    <MercadoExecuteContexts {...props} />
  ) : (
    <ExecuteContextsFieldV2Component {...props} />
  );
}

const LogisticsContent = styled.div`
  padding: 14px;
  margin-bottom: 20px;
  border: solid 1px #f0f2f5;
  border-top: none;
  > div {
    width: 260px;
    padding: 12px;
    border: solid 1px #d3d7e0;
  }
  > textarea {
    margin-bottom: 12px;
  }
`;
